import { useMemo } from 'react';
import type { MidiClip } from '@/domain/types';
import { signatureMapOf } from '@/domain/selectors';
import { snapStepTicks } from '@/timing/snap';
import { DRUM_PADS } from '@/engine/instruments/descriptors';
import { useDaw } from '@/state/store';
import { createId } from '@/util/id';

/**
 * The step sequencer.
 *
 * It is a second view of the same MIDI clip the piano roll edits — one pad per
 * row, one cell per grid step — not a parallel data model. A pattern entered
 * here opens in the piano roll and vice versa, and a drum part is an ordinary
 * clip that can be split, moved and looped like any other.
 *
 * Steps are DOM buttons rather than canvas: there are at most a few hundred,
 * each is a real click target, and being buttons means they are reachable by
 * keyboard and announce their state to a screen reader for free.
 */

const MAX_STEPS = 64;

export function DrumSequencer(): JSX.Element {
  const project = useDaw((s) => s.project);
  const selection = useDaw((s) => s.selection);
  const dispatch = useDaw((s) => s.dispatch);

  const clip = selection.clipIds.length === 1 ? project.clips[selection.clipIds[0]] : null;
  const midiClip: MidiClip | null = clip && clip.kind === 'midi' ? clip : null;
  const track = midiClip ? project.tracks[midiClip.trackId] : null;

  const sigMap = useMemo(() => signatureMapOf(project), [project]);

  const stepTicks = midiClip
    ? snapStepTicks(sigMap, project.transport.snap === 'off' ? '1/16' : project.transport.snap, midiClip.startTicks) ||
      project.transport.ppq / 4
    : project.transport.ppq / 4;

  const patternTicks = midiClip ? (midiClip.loop ? midiClip.loopLengthTicks : midiClip.durationTicks) : 0;
  const steps = midiClip ? Math.min(MAX_STEPS, Math.max(1, Math.round(patternTicks / stepTicks))) : 0;

  if (!midiClip || !track) {
    return <div className="empty-note">Select a drum clip to edit its pattern.</div>;
  }
  if (track.instrument?.type !== 'drumMachine') {
    return <div className="empty-note">This track is not running the drum machine. The piano roll edits it instead.</div>;
  }

  /** The note occupying a pad's step, if any. */
  const noteAt = (pitch: number, step: number) => {
    const from = step * stepTicks;
    const to = from + stepTicks;
    return midiClip.notes.find((note) => note.pitch === pitch && note.startTicks >= from && note.startTicks < to);
  };

  const toggle = (pitch: number, step: number, velocity: number): void => {
    const existing = noteAt(pitch, step);
    if (existing) {
      dispatch({ type: 'midi.deleteNotes', params: { clipId: midiClip.id, noteIds: [existing.id] } });
      return;
    }
    dispatch({
      type: 'midi.addNote',
      params: {
        clipId: midiClip.id,
        noteId: createId('not'),
        pitch,
        velocity,
        startTicks: Math.round(step * stepTicks),
        durationTicks: Math.max(1, Math.round(stepTicks)),
      },
    });
  };

  const beatsPerStep = stepTicks / project.transport.ppq;

  return (
    <div className="sequencer">
      <div className="sequencer__toolbar">
        <span className="field__label">Pattern</span>
        <span style={{ color: 'var(--text-0)' }}>{midiClip.name}</span>
        <span className="field__label">
          {steps} steps of {project.transport.snap === 'off' ? '1/16' : project.transport.snap}
        </span>
        <div className="topbar__spacer" />
        <button
          className={`btn ${midiClip.loop ? 'btn--active' : ''}`}
          onClick={() => dispatch({ type: 'midi.setClipLoop', params: { clipId: midiClip.id, loop: !midiClip.loop } })}
          title="Repeat the pattern for the clip's whole length"
        >
          Loop pattern
        </button>
        <button
          className="btn"
          onClick={() =>
            dispatch({
              type: 'midi.deleteNotes',
              params: { clipId: midiClip.id, noteIds: midiClip.notes.map((n) => n.id) },
            })
          }
          disabled={midiClip.notes.length === 0}
        >
          Clear
        </button>
      </div>

      <div className="sequencer__grid">
        {DRUM_PADS.map((pad) => (
          <div className="sequencer__row" key={pad.id}>
            <div className="sequencer__pad">{pad.label}</div>
            <div className="sequencer__steps">
              {Array.from({ length: steps }, (_, step) => {
                const note = noteAt(pad.pitch, step);
                // A step on the beat is marked, so a bar is countable at a glance.
                const onBeat = Math.abs((step * beatsPerStep) % 1) < 1e-6;
                const accent = note && note.velocity > 100;
                return (
                  <button
                    key={step}
                    className={[
                      'sequencer__step',
                      onBeat ? 'sequencer__step--beat' : '',
                      note ? 'sequencer__step--on' : '',
                      accent ? 'sequencer__step--accent' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={note ? { background: track.color } : undefined}
                    // Shift-click writes an accent: the same step, played harder.
                    onClick={(e) => toggle(pad.pitch, step, e.shiftKey ? 120 : 95)}
                    aria-pressed={Boolean(note)}
                    aria-label={`${pad.label} step ${step + 1}`}
                    title={note ? `Velocity ${note.velocity} — click to clear` : 'Click to add, shift-click to accent'}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
